"use client";

/**
 * RightPagesPanel (MVP)
 * - Shows Cover/Content tabs
 * - Shows page/unit tiles
 * - Click tile -> switches page/unit
 * - Shows content page count info
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragOverlay
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTranslations } from "next-intl";
import { useEditorStore } from "./editorStore";

function countPhysicalPagesFromUnits(units: any[], contentLayout: "book" | "spreads_only") {
  const spreads = units.filter((u: any) => String(u?.kind || "single") === "spread").length;
  if (contentLayout === "spreads_only") return spreads * 2;
  return 2 + spreads * 2;
}

function labelForUnit(units: any[], idx: number, layout: "book" | "spreads_only") {
  if (!units[idx]) return "";

  if (layout === "spreads_only") {
    const a = idx * 2 + 1;
    const b = idx * 2 + 2;
    return `${a}–${b}`;
  }

  if (idx === 0) return "1";
  if (idx === units.length - 1) {
    const spreadsCount = Math.max(0, units.length - 2);
    const last = 2 + 2 * spreadsCount;
    return String(last);
  }

  const a = 2 * idx;
  const b = 2 * idx + 1;
  return `${a}–${b}`;
}

function getPagePreviewWidth(formatWmm: number, kind: "single" | "spread") {
  const singleWidth = formatWmm <= 150 ? 104 : 116;
  return kind === "spread" ? singleWidth + 56 : singleWidth;
}

function PagePlusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6.5 3.5h7.2l3.8 3.8v13.2h-11z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M13.5 3.8v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M12 11.2v5.6M9.2 14h5.6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  );
}

function MenuBtn({
  icon,
  label,
  onClick,
  danger,
  disabled
}: {
  icon: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
        danger ? "text-red-400" : "text-white"
      } ${disabled ? "cursor-not-allowed opacity-50" : "hover:bg-white/5"}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="w-5">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function SortableTile({
  id,
  children
}: {
  id: string;
  children: (args: {
    setNodeRef: (element: HTMLElement | null) => void;
    style: React.CSSProperties;
    attributes: Record<string, any>;
    listeners: Record<string, any> | undefined;
    isDragging: boolean;
  }) => React.ReactNode;
}) {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1
  };

  return <>{children({ setNodeRef, style, attributes, listeners, isDragging })}</>;
}

export default function RightPagesPanel({
  maxPages,
  thumbs,
  formats,
  coverPropertiesPanel
}: {
  maxPages: number;
  thumbs: Record<string, string>;
  formats: any[];
  coverPropertiesPanel?: React.ReactNode;
}) {
  const store = useEditorStore();
  const tf = useTranslations("editor.footer");
  const tp = useTranslations("editor.pagination");
  const tr = useTranslations("editor.rightPages");
  const [gearOpen, setGearOpen] = useState<null | { key: string; top: number; left: number; idx: number }>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const tab = store.activeDoc;
  const contentData = store.contentData;
  const layoutMode = String(contentData?.meta?.layout_mode || "pages");
  const contentLayout = (String(contentData?.meta?.content_layout || "book") === "spreads_only"
    ? "spreads_only"
    : "book") as "book" | "spreads_only";
  const formatKey = String(contentData?.meta?.format_key || "");
  const format = useMemo(
    () => formats.find((f: any) => f.id === formatKey),
    [formatKey, formats]
  );
  const formatWmm = Number(format?.page_mm?.width ?? 210);
  const formatHmm = Number(format?.page_mm?.height ?? 297);

  const contentUnits = Array.isArray(contentData?.pages) ? contentData.pages : [];

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setGearOpen(null);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!activeDragId) return;

    let raf: number | null = null;
    let lastY = 0;

    function tick() {
      const el = listRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const edge = 60;
      let dy = 0;

      if (lastY < rect.top + edge) {
        dy = -Math.round((rect.top + edge - lastY) / 6);
      } else if (lastY > rect.bottom - edge) {
        dy = Math.round((lastY - (rect.bottom - edge)) / 6);
      }

      if (dy !== 0) el.scrollTop += dy;
      raf = window.requestAnimationFrame(tick);
    }

    function onPointerMove(event: PointerEvent) {
      lastY = event.clientY;
    }

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    raf = window.requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      if (raf != null) window.cancelAnimationFrame(raf);
    };
  }, [activeDragId]);

  const physicalNow = layoutMode === "book_spreads"
    ? countPhysicalPagesFromUnits(contentUnits, contentLayout)
    : contentUnits.length;
  const canAddAnotherSpread = layoutMode === "book_spreads" && physicalNow + 2 <= maxPages;

  const coverMode = String(store.coverData?.cover_meta?.cover_mode || "spread");
  const coverTiles = coverMode === "separate"
    ? [
        { label: tf("cover.back"), idx: 0 },
        { label: tf("cover.front"), idx: 1 }
      ]
    : [{ label: tf("cover.cover"), idx: 0 }];

  function getThumbAspect({
    formatWmm: thumbWmm,
    formatHmm: thumbHmm,
    kind
  }: {
    formatWmm: number;
    formatHmm: number;
    kind: "single" | "spread";
  }) {
    if (kind === "spread") return (2 * thumbWmm) / thumbHmm;
    return thumbWmm / thumbHmm;
  }

  const contentTiles = useMemo(() => {
    if (layoutMode === "book_spreads") {
      return contentUnits.map((u: any, idx: number) => {
        const kind = String(u?.kind || "single") as "single" | "spread";
        const unitId = String(u?.unit_id || `u_${idx}`);
        const key = `content:${unitId}`;
        const pageLabel = labelForUnit(contentUnits, idx, contentLayout);
        return { idx, pageLabel, kind, key, unitId };
      });
    }

    return contentUnits.map((_page: any, idx: number) => ({
      idx,
      pageLabel: String(idx + 1),
      kind: "single" as const,
      key: `content:${idx}`,
      unitId: String(idx)
    }));
  }, [contentLayout, contentUnits, layoutMode]);

  function openGear(event: React.MouseEvent, key: string, idx: number) {
    event.preventDefault();
    event.stopPropagation();

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const menuW = 220;
    const left = Math.max(12, Math.min(window.innerWidth - menuW - 12, rect.right - menuW));
    const top = rect.bottom + 8;

    setGearOpen({ key, idx, top, left });
  }

  function getContentLocks(index: number) {
    const isBook = contentLayout === "book";
    const lastIndex = contentUnits.length - 1;
    const minMovableIndex = isBook ? 1 : 0;
    const maxMovableIndex = isBook ? lastIndex - 1 : lastIndex;
    const locked = isBook && (index === 0 || index === lastIndex);
    const canDelete = isBook ? !locked : contentUnits.length > 1;

    return {
      canMoveToStart: !locked && index > minMovableIndex && maxMovableIndex >= minMovableIndex,
      canMoveToEnd: !locked && index < maxMovableIndex && maxMovableIndex >= minMovableIndex,
      canDelete,
      locked
    };
  }

  const activeLocks = gearOpen ? getContentLocks(gearOpen.idx) : null;

  function renderContentTile(tile: { idx: number; pageLabel: string; kind: "single" | "spread"; key: string; unitId: string }) {
    const active = store.contentUnitIndex === tile.idx;
    const previewWidth = getPagePreviewWidth(formatWmm, tile.kind);

    return (
      <div
        data-active={active}
        className={`editor-page-card group relative w-full cursor-grab text-left active:cursor-grabbing ${
          active
            ? "border-[#9741ff] shadow-sm"
            : ""
        }`}
      >
        <button
          type="button"
          className="absolute inset-0 z-10 rounded-xl"
          aria-label={tp("pageLabel", { label: tile.pageLabel })}
          onClick={() => {
            store.setContentUnitIndex(tile.idx);
          }}
        />

        <button
          type="button"
          className={`absolute right-2 top-2 z-20 flex h-7 w-7 items-center justify-center rounded-[8px] border text-sm transition-colors ${
            active
              ? "border-white/20 bg-[#43506a] text-white hover:bg-[#4a5872]"
              : "border-white/10 bg-[#43506a] text-white hover:bg-[#4a5872]"
          }`}
          title={tr("options")}
          aria-label={tr("options")}
          onClick={(event) => openGear(event, tile.key, tile.idx)}
        >
          ⋯
        </button>

        <div className="flex flex-col items-center gap-2">
          <div
            className="overflow-hidden rounded-[8px] bg-white shadow-[0_1px_0_rgba(15,23,42,0.08)]"
            style={{
              width: `${previewWidth}px`,
              aspectRatio: String(
                getThumbAspect({
                  formatWmm,
                  formatHmm,
                  kind: tile.kind
                })
              )
            }}
          >
            {thumbs[tile.key] ? (
              <img
                src={thumbs[tile.key]}
                className="h-full w-full object-contain"
                alt=""
                draggable={false}
              />
            ) : (
              <div className="h-full w-full bg-white" />
            )}
          </div>

          <div className="text-center text-sm font-medium text-white/90">
            {tp("pageLabel", { label: tile.pageLabel })}
          </div>
        </div>
      </div>
    );
  }

  function renderInsertBetweenButton(afterIndex: number) {
    const disabled = !canAddAnotherSpread || Boolean(activeDragId);
    const title = canAddAnotherSpread ? tf("actions.insertSpreadHere") : tf("actions.maxPagesReached");

    return (
      <div className="flex h-9 items-center justify-center" aria-hidden={activeDragId ? true : undefined}>
        <button
          type="button"
          className={`flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
            disabled
              ? "cursor-not-allowed border-white/10 bg-white/5 text-white/35"
              : "border-white/20 bg-white text-[#272727] shadow-sm hover:bg-[#272727] hover:text-white"
          }`}
          title={title}
          aria-label={title}
          disabled={disabled}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!canAddAnotherSpread) return;
            store.insertContentSpreadUnitAt(afterIndex + 1);
          }}
        >
          <PagePlusIcon />
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full flex-col overflow-hidden">
        <div className="editor-panel-header shrink-0 p-4">
          <div className="flex gap-2">
            <button
              type="button"
              className={`editor-doc-switch-button editor-secondary-button flex-1 px-3 py-1 text-xs md:px-4 md:text-sm ${
                tab === "cover" ? "" : "opacity-75 hover:opacity-100"
              }`}
              data-active={tab === "cover"}
              style={tab === "cover" ? {
                background: "linear-gradient(135deg, var(--editor-gradient-start), var(--editor-gradient-end))",
                borderColor: "#ffffffbf",
                boxShadow: "inset 0 0 0 1px #ffffff2e, 0 12px 24px #7c2cff2e"
              } : undefined}
              onClick={() => {
                store.setActiveDoc("cover");
              }}
            >
              {tf("switch.cover")}
            </button>

            <button
              type="button"
              className={`editor-doc-switch-button editor-secondary-button flex-1 px-3 py-1 text-xs md:px-4 md:text-sm ${
                tab === "content" ? "" : "opacity-75 hover:opacity-100"
              }`}
              data-active={tab === "content"}
              style={tab === "content" ? {
                background: "linear-gradient(135deg, var(--editor-gradient-start), var(--editor-gradient-end))",
                borderColor: "#ffffffbf",
                boxShadow: "inset 0 0 0 1px #ffffff2e, 0 12px 24px #7c2cff2e"
              } : undefined}
              onClick={() => {
                store.setActiveDoc("content");
              }}
            >
              {tf("switch.content")}
            </button>
          </div>

          {tab === "content" ? (
            <div className="mt-4 text-sm font-semibold text-white/90">
              {tf("stats.pages")}: <span className="font-mono text-white">{physicalNow}</span> / <span className="font-mono text-white">{maxPages}</span>
            </div>
          ) : null}
        </div>

        <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {tab === "cover"
            ? (
                <>
                  {coverTiles.map((tile) => (
                    <button
                      key={tile.label}
                      type="button"
                      className={`editor-page-card w-full text-left transition-colors ${
                        store.coverPageIndex === tile.idx
                          ? "border-[#9741ff] text-white"
                          : "text-white"
                      }`}
                      data-active={store.coverPageIndex === tile.idx}
                      onClick={() => store.setCoverPageIndex(tile.idx)}
                    >
                      <div
                        className="mx-auto rounded-[8px] bg-white shadow-[0_1px_0_rgba(15,23,42,0.08)]"
                        style={{
                          aspectRatio: String(coverMode === "spread" ? (formatWmm * 2) / formatHmm : formatWmm / formatHmm),
                          width: `${coverMode === "spread" ? getPagePreviewWidth(formatWmm, "spread") : getPagePreviewWidth(formatWmm, "single")}px`
                        }}
                      />
                      <div className="mt-2 text-center text-sm font-medium text-white/90">{tile.label}</div>
                    </button>
                  ))}

                  {coverPropertiesPanel ? (
                    <div className="border-t border-white/10 pt-4">
                      {coverPropertiesPanel}
                    </div>
                  ) : null}
                </>
              )
            : layoutMode === "book_spreads"
              ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={(event) => setActiveDragId(String(event.active.id))}
                    onDragCancel={() => setActiveDragId(null)}
                    onDragEnd={(event) => {
                      const activeId = String(event.active?.id || "");
                      const overId = event.over ? String(event.over.id) : "";
                      setActiveDragId(null);

                      if (!activeId || !overId || activeId === overId) return;
                      store.reorderContentUnits(activeId, overId);
                    }}
                  >
                    <SortableContext
                      items={contentTiles.map((tile: { unitId: string }) => tile.unitId)}
                      strategy={verticalListSortingStrategy}
                    >
                      {contentTiles.map((tile: { idx: number; pageLabel: string; kind: "single" | "spread"; key: string; unitId: string }, index: number) => (
                        <React.Fragment key={tile.unitId}>
                          <SortableTile id={tile.unitId}>
                            {({ setNodeRef, style, attributes, listeners, isDragging }) => (
                              <div ref={setNodeRef} style={style} className="relative">
                                <div
                                  {...attributes}
                                  {...listeners}
                                  className={`select-none ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
                                >
                                  {renderContentTile(tile)}
                                </div>
                              </div>
                            )}
                          </SortableTile>

                          {index < contentTiles.length - 1 ? renderInsertBetweenButton(tile.idx) : null}
                        </React.Fragment>
                      ))}
                    </SortableContext>

                    <DragOverlay>
                      {activeDragId ? (
                        <div className="editor-page-card w-[220px] text-sm text-white shadow-lg">
                          {tr("reordering")}
                        </div>
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                )
              : contentTiles.map((tile: { idx: number; pageLabel: string; kind: "single" | "spread"; key: string; unitId: string }) => (
                  <div key={tile.key}>{renderContentTile(tile)}</div>
                ))}
        </div>
      </div>

      {typeof document !== "undefined" && gearOpen
        ? createPortal(
            <div className="fixed inset-0 z-[9999]" onMouseDown={() => setGearOpen(null)}>
              <div
                className="editor-modal fixed w-[220px] p-2"
                style={{ top: gearOpen.top, left: gearOpen.left }}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="px-3 py-2 text-xs uppercase tracking-wide text-white/55">{tr("pageOptions")}</div>

                <MenuBtn
                  icon="📄"
                  label={tf("actions.duplicate")}
                  onClick={() => {
                    store.duplicateContentUnitAt(gearOpen.idx);
                    setGearOpen(null);
                  }}
                  disabled={Boolean(activeLocks?.locked)}
                />

                <MenuBtn
                  icon="⤒"
                  label={tr("moveToStart")}
                  onClick={() => {
                    store.moveContentUnitToStart(gearOpen.idx);
                    setGearOpen(null);
                  }}
                  disabled={!activeLocks?.canMoveToStart}
                />

                <MenuBtn
                  icon="⤓"
                  label={tr("moveToEnd")}
                  onClick={() => {
                    store.moveContentUnitToEnd(gearOpen.idx);
                    setGearOpen(null);
                  }}
                  disabled={!activeLocks?.canMoveToEnd}
                />

                <MenuBtn
                  icon="🗑"
                  label={tf("actions.delete")}
                  danger
                  onClick={() => {
                    store.deleteContentUnitAt(gearOpen.idx);
                    setGearOpen(null);
                  }}
                  disabled={!activeLocks?.canDelete}
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
